// Schreiben-Assistent Etappe 2 (#586): Briefköpfe (Briefpapier + Rücksendezeile + Ort) und Absender mit Unterschrift.
// Lesen alle Mitarbeiter, pflegen nur Admins (RLS). Fehlen die Tabellen noch (Migration), gilt das eingebaute
// Briefpapier und „ohne Absender“ – der Assistent arbeitet dann wie in Etappe 1.
import { supabase } from '../supabase/client';
import { RUECKSENDEZEILE, ladeBriefpapier, type BriefZusatz } from '../dokumente/blankoBrief';

export interface Briefkopf {
  id: string; name: string; bild_pfad: string | null; ruecksendezeile: string; ort: string;
  ist_standard: boolean; aktiv: boolean; sortierung: number;
}
export interface Absender {
  id: string; name: string; funktion: string | null; profile_id: string | null; unterschrift_pfad: string | null;
  aktiv: boolean; sortierung: number;
  /** eigene Schlussformel im Brief (Spalte ab Migration 2026-11-01-schreiben-grussformel) */
  grussformel?: string | null;
  /** #608: „i. A.“ vor der Unterschrift (Spalte ab Migration 2026-11-04-absender-im-auftrag) */
  im_auftrag?: boolean | null;
}

export const BRIEFKOPF_BUCKET = 'briefpapier';
export const UNTERSCHRIFT_BUCKET = 'signaturen';

/** Das eingebaute Briefpapier (Stand vor Etappe 2). */
export const EINGEBAUT: Briefkopf = {
  id: 'eingebaut', name: 'Immobilienmanagement Schwede', bild_pfad: null, ruecksendezeile: RUECKSENDEZEILE, ort: 'Kirchhain',
  ist_standard: true, aktiv: true, sortierung: 0,
};

/** Tabelle noch nicht angelegt (Migration fehlt) – nur dann gilt „leer“; jeder andere Fehler wird gemeldet,
 *  damit kein Schreiben still mit falschem Briefkopf oder ohne Unterschrift rausgeht. */
export const tabelleFehlt = (e: { code?: string } | null) => !!e && ['42P01', 'PGRST205', 'PGRST200'].includes(e.code ?? '');

export async function ladeBriefkoepfe(): Promise<Briefkopf[]> {
  const { data, error } = await supabase.from('briefkoepfe').select('*').order('sortierung').order('name');
  if (tabelleFehlt(error)) return [];
  if (error) throw new Error(`Briefköpfe nicht geladen: ${error.message}`);
  return (data ?? []) as Briefkopf[];
}

export async function ladeAbsender(): Promise<Absender[]> {
  const { data, error } = await supabase.from('brief_absender').select('*').order('sortierung').order('name');
  if (tabelleFehlt(error)) return [];
  if (error) throw new Error(`Absender nicht geladen: ${error.message}`);
  return (data ?? []) as Absender[];
}

/** Briefkopf der Vorlage, sonst der Standard, sonst der erste aktive, sonst das eingebaute Briefpapier. */
export function standardBriefkopf(liste: Briefkopf[], vorlageBriefkopfId: string | null): Briefkopf {
  const aktiv = liste.filter((k) => k.aktiv);
  return aktiv.find((k) => k.id === vorlageBriefkopfId) ?? aktiv.find((k) => k.ist_standard) ?? aktiv[0] ?? EINGEBAUT;
}

/** Der mit dem eigenen Profil verknüpfte aktive Absender, sonst keiner. */
export function standardAbsender(liste: Absender[], profileId: string | null): string | null {
  return liste.find((a) => a.aktiv && profileId && a.profile_id === profileId)?.id ?? null;
}

const blobZuDataUrl = (b: Blob) => new Promise<string>((ok, fehler) => {
  const r = new FileReader();
  r.onload = () => ok(r.result as string);
  r.onerror = () => fehler(new Error('Bild konnte nicht gelesen werden'));
  r.readAsDataURL(b);
});

const bildCache = new Map<string, Promise<string>>();
function ladeBild(bucket: string, pfad: string): Promise<string> {
  const k = `${bucket}/${pfad}`;
  if (!bildCache.has(k)) {
    const p = supabase.storage.from(bucket).download(pfad).then(({ data, error }) => {
      if (error || !data) throw new Error(`Bild nicht geladen: ${pfad}`);
      return blobZuDataUrl(data);
    });
    p.catch(() => bildCache.delete(k));
    bildCache.set(k, p);
  }
  return bildCache.get(k)!;
}

/** Nach dem Hochladen eines neuen Bilds unter demselben Pfad. */
export const vergissBild = (bucket: string, pfad: string) => { bildCache.delete(`${bucket}/${pfad}`); };

export interface PdfGestaltung { hintergrund: string; ort: string; zusatz: BriefZusatz }

/** Alles, was erzeugeBlankoBriefPdf für Briefkopf und Absender braucht. */
export async function ladePdfGestaltung(kopf: Briefkopf, absender: Absender | null): Promise<PdfGestaltung> {
  const [hintergrund, bild] = await Promise.all([
    kopf.bild_pfad ? ladeBild(BRIEFKOPF_BUCKET, kopf.bild_pfad) : ladeBriefpapier(),
    absender?.unterschrift_pfad ? ladeBild(UNTERSCHRIFT_BUCKET, absender.unterschrift_pfad) : Promise.resolve(null),
  ]);
  return {
    hintergrund, ort: kopf.ort || 'Kirchhain',
    zusatz: { ruecksendezeile: kopf.ruecksendezeile, unterschrift: absender ? { bild, name: absender.name, funktion: absender.funktion, grussformel: absender.grussformel ?? null, imAuftrag: !!absender.im_auftrag } : null },
  };
}

/** Gestaltung über die IDs (Protokoll/Fortsetzen); Unbekanntes fällt auf Standard bzw. „ohne“ zurück. */
export async function ladePdfGestaltungNachId(briefkopfId: string | null, absenderId: string | null): Promise<PdfGestaltung> {
  const [koepfe, absender] = await Promise.all([ladeBriefkoepfe(), absenderId ? ladeAbsender() : Promise.resolve([])]);
  // Fortsetzen: genau den gespeicherten Briefkopf, auch wenn er inzwischen deaktiviert ist
  const kopf = (briefkopfId && koepfe.find((k) => k.id === briefkopfId)) || standardBriefkopf(koepfe, null);
  return ladePdfGestaltung(kopf, absender.find((a) => a.id === absenderId) ?? null);
}
